import api from './api';
import {
  parseLocalDate,
  formatCalendarDate,
  formatInstant,
  isSunday,
  isSecondOrFourthSaturday,
  calculateDays,
  calculateWorkingDays,
  todayISODate,
} from '../utils/dateUtils';

const leaveService = {
  // Apply for leave
  applyLeave: (leaveData) => {
    return api.post('/leaves/apply', leaveData);
  },

  // Get current user's leaves
  getMyLeaves: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append('status', params.status);
    if (params.year) queryParams.append('year', params.year);
    const queryString = queryParams.toString();
    return api.get(`/leaves/my-leaves${queryString ? '?' + queryString : ''}`);
  },

  // Delete a recorded leave (owner: future only; scheduler: any)
  deleteLeave: (leaveId) => {
    return api.delete(`/leaves/${leaveId}`);
  },

  // Get all leaves across the team (scheduler only)
  getAllLeaves: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append('status', params.status);
    if (params.trainerId) queryParams.append('trainerId', params.trainerId);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    const queryString = queryParams.toString();
    return api.get(`/leaves${queryString ? '?' + queryString : ''}`);
  },

  // Get leave statistics for the dashboard (scheduler only)
  getLeaveStats: (year) => {
    return api.get(`/leaves/stats${year ? '?year=' + year : ''}`);
  },

  // Get approved leaves in date range (for conflict detection)
  getApprovedLeavesInRange: (startDate, endDate) => {
    return api.get(`/leaves/approved-in-range?startDate=${startDate}&endDate=${endDate}`);
  },

  // Date helpers now live in utils/dateUtils. Kept on the service for API
  // stability (existing callers use leaveService.parseLocalDate, .formatDate…).
  parseLocalDate,
  calculateDays,
  isSunday,
  isSecondOrFourthSaturday,
  calculateWorkingDays,

  // Calendar date (date-only) display — timezone-independent.
  formatDate: (dateString) => formatCalendarDate(dateString),

  // Timestamp display in IST (for "Recorded On" etc.), stable on any machine.
  // Date-only to match the column's look, but computed in IST rather than from
  // the machine clock.
  formatDateTime: (value) => formatInstant(value, { year: 'numeric', month: 'short', day: 'numeric' }),

  // Helper: Get status badge class for computed date-derived status.
  // Values: 'upcoming' | 'ongoing' | 'past'
  getStatusClass: (status) => {
    const statusMap = {
      'upcoming': 'status-upcoming',
      'ongoing': 'status-ongoing',
      'past': 'status-past',
    };
    return statusMap[status] || 'status-past';
  },

  // Compute leave status from its date range, anchored to the IST calendar date
  // (string compare) so it's independent of the viewer's machine timezone.
  computeStatus: (leave) => {
    const today = todayISODate();
    const start = String(leave.start_date).split('T')[0];
    const end = String(leave.end_date).split('T')[0];
    if (!start || !end) return 'past';
    if (start > today) return 'upcoming';
    if (end < today) return 'past';
    return 'ongoing';
  },

  // Helper: Get leave type label
  getLeaveTypeLabel: (type) => {
    const typeMap = {
      'casual': 'Casual Leave',
      'sick': 'Sick Leave',
      'personal': 'Personal Leave',
      'other': 'Other'
    };
    return typeMap[type] || type;
  }
};

export default leaveService;
